import { ReactNode } from 'react';

import { cx } from '@libs/classnames';
import { InfiniteScroll } from '@ui/components/InfiniteScroll';
import { Loader } from '@ui/components/Loader';
import { useMediaQuery } from '@providers/MediaQueryProvider';
import { Placeholder } from '@ui/components/Placeholder/Placeholder';
import { SearchInput } from '@ui/components/SearchInput';
import { Table, TableCell, TableRow } from '@ui/components/Table';
import { Tabs } from '@ui/components/Tabs';

import { ClockControlType, TopPlayer } from '@features/shared/api/typings';

import styles from './TopPlayersList.module.css';

const TABS: { value: ClockControlType; label: ReactNode }[] = [
  { value: 'Blitz', label: 'Блиц' },
  { value: 'Rapid', label: 'Рапид' },
];

type Props = {
  className?: string;
  listClassName?: string;
  topPlayers?: TopPlayer[];
  self?: TopPlayer;
  clockControlType: ClockControlType;
  onClockControlTypeChange: (newClockControl: ClockControlType) => void;
  query?: string;
  onQueryChange?: (newQuery: string) => void;
  currentUserId?: string;
  onLoadMore: () => void;
  hasMore: boolean;
  groupTitle?: string | null;
  hoverable?: boolean;
  isLoading?: boolean;
  renderShieldQuickPeek?: (userId: string) => ReactNode;
};
export function TopPlayersList({
  className,
  listClassName,
  topPlayers,
  self,
  clockControlType,
  onClockControlTypeChange,
  query,
  onQueryChange,
  currentUserId,
  onLoadMore,
  hasMore,
  groupTitle,
  hoverable,
  isLoading,
  renderShieldQuickPeek,
}: Props) {
  const isMobile = useMediaQuery() === 'mobile';

  const addSelfToTheList =
    self &&
    !query &&
    topPlayers &&
    topPlayers.length > 0 &&
    topPlayers[topPlayers.length - 1] &&
    self.clubRank > topPlayers[topPlayers.length - 1]!.clubRank;

  return (
    <div className={cx(styles.topPlayersList, className)}>
      <div className={styles.header}>
        <Tabs
          tabClassName={styles.tab}
          flat
          tabs={TABS}
          value={clockControlType}
          onChange={onClockControlTypeChange}
        />

        {onQueryChange && (
          <SearchInput
            query={query}
            onQueryChange={onQueryChange}
            placeholder="Поиск по участникам"
          />
        )}
      </div>

      <InfiniteScroll
        scrollableClassName={cx(styles.listContainer, listClassName)}
        fetchMore={onLoadMore}
        hasMore={hasMore}
        itemsLength={topPlayers?.length || 0}
      >
        <Table
          className={cx(styles.list, { [styles.withGroups]: !!groupTitle })}
          borderSpacing="none"
        >
          <TableRow header>
            <TableCell className={styles.numberColumn}></TableCell>
            {isMobile ? (
              <TableCell>Имя</TableCell>
            ) : (
              <>
                <TableCell>Имя</TableCell>
                <TableCell className={styles.gamesColumn} right>
                  Игры&nbsp;·&nbsp;Победы&nbsp;·&nbsp;%
                </TableCell>
              </>
            )}
            <TableCell className={styles.rankColumn} right>
              Рейтинг
            </TableCell>
          </TableRow>

          {topPlayers?.map((topPlayer) => (
            <TopPlayersListItem
              key={topPlayer.member.userId}
              userId={topPlayer.member.userId}
              number={topPlayer.clubRank}
              name={topPlayer.member.lastName + ' ' + topPlayer.member.firstName}
              groupName={groupTitle ? topPlayer.member.groupName || ' ' : undefined}
              gamesCount={topPlayer.gamesCount}
              winsCount={topPlayer.winsCount}
              highlighted={currentUserId === topPlayer.member.userId}
              rank={topPlayer.clubRating}
              isMobile={isMobile}
              hoverable={hoverable}
              renderShieldQuickPeek={renderShieldQuickPeek}
            />
          ))}

          {addSelfToTheList && (
            <TopPlayersListItem
              key={self.member.userId}
              userId={self.member.userId}
              number={self.clubRank}
              name={self.member.lastName + ' ' + self.member.firstName}
              groupName={groupTitle ? self.member.groupName || ' ' : undefined}
              gamesCount={self.gamesCount}
              winsCount={self.winsCount}
              highlighted={currentUserId === self.member.userId}
              rank={self.clubRating}
              isMobile={isMobile}
              renderShieldQuickPeek={renderShieldQuickPeek}
            />
          )}
        </Table>

        {!topPlayers?.length &&
          ((isLoading && <Loader centered />) || (
            <Placeholder>{query ? 'Игроки не найдены' : 'Пока топ игроков нет'}</Placeholder>
          ))}
      </InfiniteScroll>
    </div>
  );
}

type TopPlayersListItemProps = {
  className?: string;
  userId: string;
  number: number;
  name: string;
  groupName?: string;
  gamesCount: number;
  winsCount: number;
  rank: number;
  highlighted: boolean;
  isMobile?: boolean;
  hoverable?: boolean;
  renderShieldQuickPeek?: (userId: string) => ReactNode;
};
export function TopPlayersListItem({
  className,
  userId,
  number,
  name,
  groupName,
  gamesCount,
  winsCount,
  rank,
  highlighted,
  isMobile,
  hoverable,
  renderShieldQuickPeek,
}: TopPlayersListItemProps) {
  const winsPercent = gamesCount > 0 ? Math.floor((winsCount / gamesCount) * 100) : 0;

  return (
    <TableRow hoverable={hoverable} active={highlighted} className={cx(styles.player, className)}>
      <TableCell top className={styles.numberColumn}>
        {number}
      </TableCell>
      <TableCell top className={styles.nameColumn}>
        <div className={styles.nameContainer}>
          <div className={styles.name}>{name}</div>
          {renderShieldQuickPeek?.(userId)}
        </div>

        {groupName ? <div className={styles.groupName}>{groupName}</div> : null}
      </TableCell>
      {!isMobile && (
        <>
          <TableCell top right className={styles.gamesColumn}>
            {gamesCount}&nbsp;·&nbsp;{winsCount}&nbsp;·&nbsp;{winsPercent}
          </TableCell>
        </>
      )}
      <TableCell top right>
        {rank}
      </TableCell>
    </TableRow>
  );
}
